﻿import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AdminButton, AdminCard, AdminErrorBanner, AdminPageLayout } from "@/components/admin";
import SummaryCards from "./components/SummaryCards";
import AccountTable from "./components/AccountTable";
import AccountDetailPanel from "./components/AccountDetailPanel";
import QuickFilters from "./components/QuickFilters";
import InfoCard from "./components/InfoCard";
import { deleteMemberAccount, fetchMemberDetail, fetchMembers, updateMemberStatus } from "../../../lib/api";
import type { MemberDetail, MemberItem } from "@/types.ts";

const accountIdOf = (member: Pick<MemberItem, "userAccountId">): number | null => {
  return member.userAccountId;
};

const accountKeyOf = (member: Pick<MemberItem, "userAccountId">): string | null => {
  const accountId = accountIdOf(member);
  return accountId == null ? null : String(accountId);
};

const applyFilter = (members: MemberItem[], filter: string, query: string): MemberItem[] => {
  let result = [...members];

  if (filter !== "all") {
    result = result.filter((member) => member.userType === filter || member.status === filter || member.accountType === filter);
  }

  if (query.trim()) {
    const normalizedQuery = query.trim().toLowerCase();
    result = result.filter((member) =>
      [member.name, member.loginId, member.phone, String(member.userAccountId)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery)),
    );
  }

  return result;
};

const MembersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedAccountId = Number(searchParams.get("userAccountId"));
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<MemberDetail | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reasonCode, setReasonCode] = useState("OPERATOR_REQUEST");
  const [reason, setReason] = useState("");
  const [auditMessage, setAuditMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadMembers = useCallback(
    async (preferredKey?: string | null) => {
      setLoadingList(true);
      setErrorMessage(null);
      try {
        const nextMembers = await fetchMembers();
        setMembers(nextMembers);
        const requestedKey = Number.isFinite(requestedAccountId) && requestedAccountId > 0 ? String(requestedAccountId) : null;
        const candidateKey = preferredKey ?? requestedKey;
        const nextPreferredKey = candidateKey != null && nextMembers.some((member) => accountKeyOf(member) === candidateKey)
          ? candidateKey
          : null;
        if (nextPreferredKey != null) {
          setSelectedKey(nextPreferredKey);
        } else if (selectedKey == null && nextMembers.length > 0) {
          setSelectedKey(accountKeyOf(nextMembers[0]));
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : "계정 목록을 불러오지 못했습니다.");
      } finally {
        setLoadingList(false);
      }
    },
    [requestedAccountId, selectedKey],
  );

  useEffect(() => {
    void loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const state = location.state as { elderLoginId?: string; elderId?: string; globalSearchKeyword?: string } | null;
    const nextSearchQuery = state?.globalSearchKeyword ?? state?.elderLoginId;
    if (nextSearchQuery) {
      setSearchQuery(nextSearchQuery);
      navigate("/admin/users", { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedKey == null) {
      setDetail(null);
      return;
    }
    const selectedAccountId = Number(selectedKey);
    if (!Number.isFinite(selectedAccountId)) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setErrorMessage(null);

    fetchMemberDetail(selectedAccountId)
      .then((nextDetail) => {
        if (!cancelled) setDetail(nextDetail);
      })
      .catch((error) => {
        if (!cancelled) {
          setDetail(null);
          setErrorMessage(error instanceof Error ? error.message : "계정 상세를 불러오지 못했습니다.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedKey]);

  const handleStatusChange = async (nextStatus: string) => {
    if (!detail || reason.trim().length < 10) return;
    const accountId = accountIdOf(detail);
    if (accountId == null) return;
    setSaving(true);
    setErrorMessage(null);
    setAuditMessage(null);
    try {
      const result = await updateMemberStatus(accountId, nextStatus, reasonCode, reason.trim());
      const nextDetail = await fetchMemberDetail(accountId);
      setDetail(nextDetail);
      setReason("");
      setAuditMessage(`감사 #${result.audit.auditLogId} · ${result.audit.performedAt} · ${result.audit.requestId}`);
      await loadMembers(accountKeyOf(detail));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "계정 상태를 변경하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (confirmationText: string) => {
    if (!detail || reason.trim().length < 10 || confirmationText !== String(detail.userAccountId)) return;
    setSaving(true);
    setErrorMessage(null);
    setAuditMessage(null);
    try {
      const result = await deleteMemberAccount(detail.userAccountId, {
        reasonCode,
        reason: reason.trim(),
        confirmationText,
      });
      setSelectedKey(null);
      setDetail(null);
      setReason("");
      setAuditMessage(`삭제 감사 #${result.audit.auditLogId} · ${result.audit.performedAt} · ${result.audit.requestId}`);
      await loadMembers(null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "계정을 삭제하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const filteredMembers = useMemo(
    () => applyFilter(members, activeFilter, searchQuery),
    [members, activeFilter, searchQuery],
  );

  useEffect(() => {
    if (selectedKey != null && filteredMembers.some((member) => accountKeyOf(member) === selectedKey)) return;
    setSelectedKey(filteredMembers.length > 0 ? accountKeyOf(filteredMembers[0]) : null);
  }, [filteredMembers, selectedKey]);

  return (
    <AdminPageLayout
      title="Senior·Keeper 사용자 관리"
      subtitle="Senior·Keeper 앱 계정 조회, 상태 관리와 역할별 상세 진입"
      actions={
        <AdminButton variant="soft" size="sm" icon="ri-refresh-line" onClick={() => void loadMembers(detail ? accountKeyOf(detail) : null)}>
          새로고침
        </AdminButton>
      }
    >
      <AdminErrorBanner message={errorMessage} />

      <AdminCard className="mb-4 border-blue-100 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <i className="ri-information-line mt-0.5 text-blue-600" />
          <div>
            <p className="text-sm font-bold text-blue-900">현재 관리 범위</p>
            <p className="mt-1 text-xs leading-5 text-blue-700">
              이 화면은 현행 `/api/v1/admin/members`가 제공하는 Senior·Keeper `USER_ACCOUNT`를 관리합니다. 관리자 운영계정은 현재 API 목록에 포함되지 않으므로 임의로 표시하지 않습니다.
            </p>
          </div>
        </div>
      </AdminCard>

      <section className="mb-4">
        <SummaryCards members={members} />
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="xl:col-span-3">
          <AccountTable
            members={filteredMembers}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            activeFilter={activeFilter}
            loading={loadingList}
          />
        </div>

        <div className="xl:col-span-1 flex flex-col gap-4">
          <AccountDetailPanel
            detail={detail}
            loading={loadingDetail}
            saving={saving}
            reasonCode={reasonCode}
            reason={reason}
            auditMessage={auditMessage}
            onClose={() => setSelectedKey(null)}
            onReasonCodeChange={setReasonCode}
            onReasonChange={setReason}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
          <QuickFilters activeFilter={activeFilter} onFilterChange={setActiveFilter} />
          <InfoCard />
        </div>
      </div>
    </AdminPageLayout>
  );
};

export default MembersPage;
